import React, { useEffect, useRef } from 'react';

import { browserHistory } from 'react-router';

import { type Company, CompanyContext } from 'common/containers/CompanyContainer';
import { GetExperimentGroupsContext } from 'common/containers/ExperimentContainer';
import { type Viewer, ViewerContext } from 'common/containers/ViewerContainer';
import cloneElementWithProps from 'common/core/cloneElementWithProps';
import withContexts from 'common/util/withContexts';

import mixpanelInit from './mixpanel';

// Add mixpanel to the window
declare global {
  interface Window {
    mixpanel: any;
  }
}

type TrackMixpanelEvent = (eventName: string, eventData: Record<string, unknown>) => void;
export const TrackMixpanelEventContext = React.createContext<TrackMixpanelEvent>(() => {});

type GetExperimentGroups = () => Record<string, string>;

const MixpanelID = __DEV__
  ? 'c2641a425e9220f785d0be3c55d8ec2c'
  : 'ac70ebddf8b9618e6a8365f61234d2a4';

function loadMixpanelSDK() {
  if (window.mixpanel) {
    return;
  }

  mixpanelInit();

  window.mixpanel.init(MixpanelID, {
    debug: false,
  });
}

// only track members to avoid hitting event limits
const shouldTrack = (viewer: Viewer) => {
  return viewer?._id && viewer.companies && viewer.companies.length > 0;
};

const identifyUser = (viewer: Viewer, getExperimentGroups: GetExperimentGroups) => {
  const experimentGroups = getExperimentGroups();

  window.mixpanel.identify(viewer._id);
  window.mixpanel.people.set({
    ...experimentGroups,
    ...(viewer.avatarURL && {
      $avatar: viewer.avatarURL,
    }),
    $email: viewer.email,
    $name: viewer.name,
  });

  if (!viewer.companies) {
    return;
  }

  window.mixpanel.set_group(
    'company_id',
    viewer.companies.map((company) => company._id)
  );

  viewer.companies.forEach((company) => {
    window.mixpanel.get_group('company_id', company._id).set({
      $name: company.name,
      subdomain: company.subdomain,
    });
  });
};

type Props = {
  company: Company;
  property: string;
  trackPageViews: boolean;
  viewer: Viewer;
  children: React.ReactNode;
  getExperimentGroups: GetExperimentGroups;
};

const MixpanelContainer = ({
  company,
  property,
  trackPageViews = false,
  viewer,
  children,
  getExperimentGroups,
  ...props
}: Props) => {
  const trackingEnabledRef = useRef(false);
  const routeListeningRef = useRef(false);

  // Identify user and set groups
  useEffect(() => {
    if (!shouldTrack(viewer) || trackingEnabledRef.current) {
      return;
    }

    loadMixpanelSDK();
    identifyUser(viewer, getExperimentGroups);
    trackingEnabledRef.current = true;
  }, [viewer, getExperimentGroups]);

  // Listen to route changes and track page views
  useEffect(() => {
    const onRouteChange = () => {
      loadMixpanelSDK();

      window.mixpanel.track('pageview', {
        host: window.location.host,
        href: window.location.href,
        pathname: window.location.pathname,
        property: property,
        search: window.location.search,
        title: document.title,
      });
    };

    let _unlistenHistory: () => void;
    if (shouldTrack(viewer) && trackPageViews) {
      _unlistenHistory = browserHistory.listen(onRouteChange);
    }

    // Track the initial route, but only once
    if (!routeListeningRef.current) {
      onRouteChange();
      routeListeningRef.current = true;
    }

    return () => {
      if (_unlistenHistory) {
        _unlistenHistory();
      }
    };
  }, [trackPageViews, viewer, property]);

  const trackEvent = (eventName: string, eventData: Record<string, unknown>) => {
    if (!shouldTrack(viewer)) {
      return;
    }

    loadMixpanelSDK();

    const experimentGroups = getExperimentGroups();

    window.mixpanel.track(eventName, {
      property: property,
      ...eventData,
      ...experimentGroups,
    });
  };

  return (
    <TrackMixpanelEventContext.Provider value={trackEvent}>
      {cloneElementWithProps(children, props)}
    </TrackMixpanelEventContext.Provider>
  );
};

export default withContexts({
  company: CompanyContext,
  getExperimentGroups: GetExperimentGroupsContext,
  viewer: ViewerContext,
})(MixpanelContainer);
